import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { formatWeight } from '@vj/shared';
import { api } from '../../lib/api.js';
import { useListQuery } from '../../hooks/useListQuery.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { VoucherTabs } from './VoucherTabs.jsx';

const COLUMNS = [
  { key: 'voucher_no', header: 'Voucher #', sortKey: 'voucher_no' },
  { key: 'receipt_date', header: 'Date', sortKey: 'receipt_date', render: (r) => new Date(r.receipt_date).toLocaleDateString('en-IN') },
  { key: 'received_from', header: 'Received from' },
  { key: 'issue_voucher_no', header: 'Issue voucher' },
  { key: 'total_net_weight', header: 'Net weight', align: 'right', render: (r) => formatWeight(r.total_net_weight) },
  {
    key: 'has_mismatch',
    header: 'Mismatch',
    render: (r) =>
      r.has_mismatch ? (
        <span className="inline-flex items-center gap-1 text-danger">
          <AlertTriangle size={14} aria-hidden="true" /> {formatWeight(Math.abs(r.weight_difference))}
        </span>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
];

export default function ReceiptsListPage() {
  const [query, onQueryChange] = useListQuery({ sort: 'receipt_date' });
  const debouncedSearch = useDebouncedValue(query.search, 350);
  const navigate = useNavigate();

  const listQuery = useQuery({
    queryKey: ['receipts', { ...query, search: debouncedSearch }],
    queryFn: () => api.get('/receipts', { query: { ...query, search: debouncedSearch } }),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Issue &amp; receipt vouchers</h1>
      </div>

      <VoucherTabs />

      <DataTable
        columns={COLUMNS}
        data={listQuery.data?.data ?? []}
        meta={listQuery.data?.meta}
        query={query}
        onQueryChange={onQueryChange}
        isLoading={listQuery.isLoading}
        rowKey={(row) => row.receipt_id}
        onRowClick={(row) => navigate(`/issue-receipt/receipts/${row.receipt_id}`)}
        searchPlaceholder="Search by voucher number, name or issue voucher…"
        emptyLabel="No receipt vouchers yet."
      />
    </div>
  );
}
